import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { CreateOrderInvoicePayload, InvoiceGenerationResult } from './types';

const getEndpoint = (orderId: number) => `ums/v1/cms/reconciliation/orders/${orderId}/invoice`;

type TData = InvoiceGenerationResult;
type TError = AxiosError;
type TVariables = CreateOrderInvoicePayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/** Backfills the proforma invoice for one order (pre-rollout orders have none): generate
 * server-side, or upload an existing PDF. Closed months allowed by design. BE errors to
 * surface verbatim: 404 order, 400 pending terms/canceled, 409 already invoiced. */
export function useCreateOrderInvoiceApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ orderId, file }: TVariables) => {
    try {
      const body = new FormData();
      if (file) body.append('file', file);

      const { data } = await http.post(getEndpoint(orderId), body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { ...options, mutationKey: ['reconciliation-create-order-invoice'] });
}
